export const makePropertiesObj = (
	dashboardContainer,
	orgId,
	dashboardContainerId,
	dashboardId,
	dashboards
) => {
	try {
		let container = dashboardContainer
			? dashboardContainer.find(container => container.id === dashboardContainerId)
			: {};
		let dashboard = dashboards ? dashboards.find(dashboard => dashboard.id === dashboardId) : {};
		return {
			orgId: orgId,
			dashboardContainerName: container ? container.name : "",
			dashboardName: dashboard ? dashboard.name : ""
		};
	} catch (e) {
		console.log("could not make the property object due to ", e);
	}
};

const editingRoles = ["ORGADMIN", "DASHBOARDADMIN", "CANVASADMIN", "SYSADMIN", "UNIQUSADMIN"];
const viewingRoles = [...editingRoles, "DASHBOARDVIEWER", "MANAGER"];

/**
 * checks whether the a user is allowed to edit in QB and Canvas.
 */
export const isEditAccessible = roles => {
	if (typeof roles == "string") {
		return editingRoles.indexOf(roles) !== -1;
	} else if (Array.isArray(roles)) {
		return roles.some(r => editingRoles.indexOf(r) >= 0);
	} else {
		return false;
	}
};

/**
 * checks whether the a user is allowed to view in QB and Canvas.
 */
export const isViewAccessible = roles => {
	if (typeof roles == "string") {
		return viewingRoles.indexOf(roles) !== -1;
	} else if (Array.isArray(roles)) {
		return roles.some(r => viewingRoles.indexOf(r) >= 0);
	} else {
		return false;
	}
};

/**
 *
 * @param {String} email
 * @returns {Boolean} true if the email includes "@uniqus.com"
 */
export const checkIfGooderaUser = email => email && email.includes("@uniqus.com");

/**
 * INPUT: array, integer specifying chunkSize
 * OUTPUT: array of overlapping chunks
 * EXAMPLE: [1, 2, 3, 4] with chunkSize = 2 => [[1, 2], [2, 3], [3, 4]]
 */
export const createOverlappingChunk = (array, chunkSize = 2) => {
	const chunkedArray = [];
	for (let i = 0; i < array.length - (chunkSize - 1); i++) {
		chunkedArray.push(array.slice(i, i + chunkSize));
	}
	return chunkedArray;
};

/**
 * INPUT: data <OBJECT>, name of key <STRING>
 * OUTPUT: <BOOLEAN> returns true only if data[key] an array with atleast one element
 */
export const isValidArray = (data, key) => data && Array.isArray(data[key]) && data[key].length > 0;

/**
 *
 * @param {String} url (considering bluck download case where commas seprated URLs are to be checked)
 * @output {Boolean} match with the REGEX to check if the string is a valid URL
 */
export const isValidURL = url =>
	typeof url === "string" &&
	url
		.split(", ")
		.filter(link => link)
		.every(link =>
			/^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
				link
			)
		);
// ------------------------------------------------------------------------------------
// ** Following two function are used for Bulk Download functionality
// ** feel free to update if needed
const prepareFilesObject = (urlArray, folderName = "Folder") => ({
	filesObject: {
		[folderName + new Date().toISOString()]: {
			files: urlArray.map(url => ({
				url,
				name: url.split("_")[1]
			}))
		}
	}
});

export const bulkDownload = (e, url, folderName) => {
	if (url.includes(", ")) {
		e.preventDefault();
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: localStorage["$LoopBack$accessTokenId"]
			},
			body: JSON.stringify(prepareFilesObject(url.split(", "), folderName))
		};

		const now = new Date();
		const actionId = "id" + now.getMilliseconds() + now.getSeconds() + now.getMinutes();

		fetch(`https://bial-api.uniqus.com/drax/filesDownload/${actionId}/getZippedFiles`, requestOptions)
			.then(response => response.json())
			.then(result => {
				location.href = result.url;
			})
			.catch(error => console.log(error));
	} else {
		return true;
	}
};
// --------------------------------------------------------------------------------------------------
// --------------------------------------------------------------------------------------------------
// -------- function used to filter out all DCs with no Db as final child ---------------------------
// returns all immediate and non-immediate parents of db
export const getAllParents = (dcId, DCIdObjMap, allValidParentDcIds = []) => {
	// Base case: if dcId is null or undefined
	if (!dcId) {
		return allValidParentDcIds;
	}

	// Base case: if we've already seen this dcId (prevents circular references)
	if (allValidParentDcIds.includes(dcId)) {
		return allValidParentDcIds;
	}

	// Add current dcId to the list
	allValidParentDcIds.push(dcId);

	// Get the parent ID
	const parentId = DCIdObjMap?.[dcId]?.parentId;

	// Recursively get parents if there's a parent ID
	if (parentId) {
		getAllParents(parentId, DCIdObjMap, allValidParentDcIds);
	}

	return allValidParentDcIds;
};

// filter out all DC with no Db child
export const filterDC = (dc, db) => {
	// Create a map of DC objects by their IDs
	const dcIdMap = dc.reduce((map, obj) => {
		map[obj.id || obj._id] = obj;
		return map;
	}, {});

	// Get all valid DC IDs from each dashboard
	const allValidDcIds = db.reduce((acc, obj) => {
		if (obj.dashboardContainerId) {
			const parents = getAllParents(obj.dashboardContainerId, dcIdMap);
			acc.push(...parents);
		}
		return acc;
	}, []);

	// Remove duplicates and return
	return [...new Set(allValidDcIds)];
};
// ------------------------------------------------------------------------------------------------

//------- This function returns the default properties for Appcues -----------------//
export const makeAppcuesUserProperties = props => {
	const {
		userInfo: { orgRole, email }
	} = props;
	var userType = "";
	let userId = localStorage.getItem("$LoopBack$currentUserId");
	if (email && email.includes("uniqus")) {
		userType = "Internal";
	} else {
		userType = "External";
	}
	var appId = "";
	if (window.location.pathname.includes("queryBuilder")) {
		appId = "query builder";
	} else if (window.location.pathname.includes("dashboardBuilder")) {
		appId = "dashboard builder";
	}
	var appcuesIdentifyObject = {
		userId: userId,
		userType: userType,
		organizationRole: orgRole,
		appId: appId,
		queryId: null,
		queryName: null,
		ui_screen: null,
		ui_context: null,
		widgetName: null
	};
	return appcuesIdentifyObject;
};

/**
 * List of all the pages where dashboard nav is to be shown
 */
export const pagesWithNavigation = ["editor", "preview", "newEditor"];
